/**
 * SampleRoundPhotos — horizontal photo strip for one sample round.
 *
 * Sits inside each row of SamplesCard. Renders thumbnails for any photos
 * attached to the round + an Add tile when canEdit. When `onOpenPhoto` is
 * provided (the SamplesCard path), a thumb opens the in-app lightbox at that
 * gallery index; without it, the thumb falls back to opening the signed URL in
 * a new tab (backward-compatible). Trash icon on hover (canEdit only).
 *
 * Upload flow lives in the shared `useSamplePhotoUpload` hook so the cover
 * thumbnail and this strip stay byte-identical.
 *
 * Multi-photo per round: backed by `product_dev_sample_photos` (BLU-roadmap
 * pre-go-live PR2). The existing `product_dev_samples.photo_file_id` cover
 * thumb stays — this gallery is additive.
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon, Plus, Trash2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useSampleRoundPhotos,
  useProductDevMutations,
} from '../hooks/useProductDev';
import {
  useSamplePhotoUpload,
  SAMPLE_PHOTO_ACCEPTED,
} from '../hooks/useSamplePhotoUpload';

interface Props {
  sampleRoundId: string;
  canEdit: boolean;
  /**
   * When provided, thumbs open the in-app lightbox at this gallery index
   * instead of opening the signed URL in a new browser tab.
   */
  onOpenPhoto?: (index: number) => void;
}

export function SampleRoundPhotos({ sampleRoundId, canEdit, onOpenPhoto }: Props) {
  const { t } = useTranslation('productDev');
  const photosQuery = useSampleRoundPhotos(sampleRoundId);
  const { deleteSamplePhoto } = useProductDevMutations();
  const { uploading, selectFile } = useSamplePhotoUpload(sampleRoundId);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Ids whose <img> failed to decode (dead URL / undecodable HEIC) → placeholder.
  const [erroredIds, setErroredIds] = useState<Set<string>>(new Set());

  const photos = photosQuery.data ?? [];

  if (!canEdit && photos.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 flex-wrap pt-1">
      {photos.map((p, index) => {
        const thumbInner =
          p.signed_url && !erroredIds.has(p.id) ? (
            <img
              src={p.signed_url}
              alt={p.caption ?? t('samples.samplePhotoAlt')}
              className="h-full w-full object-cover"
              onError={() =>
                setErroredIds((prev) => {
                  const next = new Set(prev);
                  next.add(p.id);
                  return next;
                })
              }
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center">
              <ImageIcon className="h-3 w-3 text-muted-foreground/40" />
            </div>
          );
        const thumbClass =
          'block h-10 w-10 rounded border border-border bg-muted overflow-hidden hover:opacity-80 transition-opacity motion-reduce:transition-none';

        return (
          <div key={p.id} className="relative group">
            {onOpenPhoto ? (
              <button
                type="button"
                onClick={() => onOpenPhoto(index)}
                className={cn(
                  thumbClass,
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                )}
                title={p.caption ?? t('samples.openPhoto')}
                aria-label={p.caption ?? t('samples.openPhoto')}
              >
                {thumbInner}
              </button>
            ) : (
              <a
                href={p.signed_url ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                className={thumbClass}
                title={p.caption ?? t('samples.openPhoto')}
              >
                {thumbInner}
              </a>
            )}
          {canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (!confirm(t('samples.confirmDeletePhoto'))) return;
                deleteSamplePhoto.mutate({ photoId: p.id });
              }}
              className={cn(
                'absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-destructive-foreground',
                'flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity',
              )}
              aria-label={t('samples.deletePhoto')}
            >
              <Trash2 className="h-2.5 w-2.5" />
            </button>
          )}
          </div>
        );
      })}

      {canEdit && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={SAMPLE_PHOTO_ACCEPTED}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void selectFile(f);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
            className="sr-only"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className={cn(
              'h-10 w-10 rounded border-2 border-dashed border-muted-foreground/30',
              'flex items-center justify-center text-muted-foreground/50',
              'hover:border-threadheads-purple/40 hover:text-threadheads-purple transition-colors motion-reduce:transition-none',
              uploading && 'cursor-wait',
            )}
            aria-label={t('samples.addPhoto')}
            title={t('samples.addPhoto')}
          >
            {uploading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
          </button>
        </>
      )}
    </div>
  );
}
